import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  MenuBook,
  Gavel,
  Security,
  Print,
  Link,
  CheckCircle,
  WarningAmber,
  ArrowUpward,
  Close,
  MailOutline,
  ExpandMore,
  ChevronRight,
  InfoOutlined,
} from "@mui/icons-material";
import "./termsOfService.css";

/*
 * ============================================================
 * UNIVERSE INVENTORY — TERMS CONFIGURATION
 * ============================================================
 *
 * Keep commercial/legal values here so they can be changed
 * without modifying the document structure.
 *
 * Empty values are intentionally displayed as configurable
 * placeholders instead of inventing legal/business facts.
 */
const termsConfig = {
  companyName: "Universe Inventory",
  documentTitle: "Terms of Service",

  effectiveDate: "",
  lastUpdated: "",
  version: "1.0",
  status: "Current",

  governingLaw: "",
  jurisdiction: "",

  supportEmail: "",
  legalEmail: "",
  generalEmail: "",

  privacyPolicyUrl: "",
  refundPolicyUrl: "",

  serviceAvailability: "",
  dataRetentionPeriod: "",

  businessAddress: "",
};


/*
 * ============================================================
 * HELPER
 * ============================================================
 */

const displayConfigValue = (value, fallback = "To be configured") =>
  value && String(value).trim() ? value : fallback;


/*
 * ============================================================
 * TERMS DATA
 * ============================================================
 *
 * The actual document is data-driven. This makes it easier to
 * update, reorder, or add legal provisions later.
 */
const sections = [
  {
    id: "acceptance",
    number: "01",
    title: "Acceptance of Terms",
    paragraphs: [
      `These Terms of Service ("Terms") govern your access to and use of ${termsConfig.companyName}, including its websites, applications, software, dashboards, point-of-sale functionality, inventory-management features, business-management tools, APIs, integrations, and related services (collectively, the "Service").`,
      `By creating an account, accessing the Service, subscribing to a plan, or otherwise using the Service, you acknowledge that you have read and understood these Terms and agree to be bound by them to the extent permitted by applicable law.`,
      "If you do not agree with these Terms, you should not create an account or use the Service.",
    ],
    callouts: [
      {
        type: "warning",
        title: "Please read carefully",
        text: "These Terms establish the legal framework for your use of Universe Inventory, including accounts, subscriptions, business data, transactions, security, suspension, and termination.",
      },
    ],
  },

  {
    id: "about-universe-inventory",
    number: "02",
    title: "About Universe Inventory",
    paragraphs: [
      `${termsConfig.companyName} is a cloud-based software platform designed to help businesses manage inventory, products, sales, purchases, point-of-sale operations, business records, payments, reporting, and related business operations.`,
      "The Service may include different features, modules, plans, integrations, interfaces, and functionality. Features may change over time as the platform is improved, maintained, or developed.",
      "The availability of particular features may depend on the subscription plan, configuration, technical environment, geographic availability, third-party services, or other applicable conditions.",
    ],
  },

  {
    id: "eligibility",
    number: "03",
    title: "Eligibility",
    paragraphs: [
      "You may use the Service only if you are legally capable of entering into a binding agreement under applicable law.",
      "If you use Universe Inventory on behalf of a company, organization, partnership, business, or other entity, you represent that you have authority to accept these Terms on that entity's behalf.",
      "The Service is intended for legitimate business and operational purposes. You are responsible for ensuring that your use of the Service complies with applicable laws and regulations.",
    ],
  },

  {
    id: "account-registration",
    number: "04",
    title: "Account Registration",
    paragraphs: [
      "Some features require you to create an account. You agree to provide information that is accurate, complete, and reasonably current and to update that information when necessary.",
      "You must not knowingly provide misleading registration information or create an account for an unlawful purpose.",
      "You are responsible for activity conducted through your account unless applicable law provides otherwise.",
    ],
    bullets: [
      "Provide accurate account and business information.",
      "Maintain current contact information where required.",
      "Use the account only for authorized purposes.",
      "Do not knowingly create fraudulent or misleading accounts.",
      "Notify Universe Inventory if information necessary to secure or operate the account changes.",
    ],
  },

  {
    id: "business-information",
    number: "05",
    title: "Business Information",
    paragraphs: [
      "Universe Inventory may allow you to create and manage information relating to your business, branches, warehouses, products, customers, suppliers, employees, transactions, payments, expenses, and other operational records.",
      "You are responsible for ensuring that the information you enter is accurate, lawful, appropriately authorized, and suitable for your intended business use.",
      "You should maintain appropriate internal procedures for reviewing important business records and maintaining independent records where appropriate.",
    ],
  },

  {
    id: "account-security",
    number: "06",
    title: "Account Security",
    paragraphs: [
      "You are responsible for protecting credentials associated with your account, including passwords, authentication credentials, POS credentials, PINs, access codes, and other security information.",
      "You should not share authentication credentials unnecessarily or permit unauthorized persons to access administrative or financial functionality.",
      "If you suspect unauthorized access, credential compromise, fraudulent activity, or another security incident affecting your account, you should take reasonable steps to secure the account and contact the appropriate Universe Inventory support channel.",
    ],
    callouts: [
      {
        type: "info",
        title: "Account responsibility",
        text: "Protect your account credentials, POS PINs, authentication information, and other access controls. Actions performed through authorized credentials may be attributed to your account unless applicable law provides otherwise.",
      },
    ],
  },

  {
    id: "subscription-plans",
    number: "07",
    title: "Subscription Plans",
    paragraphs: [
      "Universe Inventory may offer different subscription plans with different features, limits, storage allowances, user allowances, transaction capabilities, or other usage conditions.",
      "The features and limits applicable to your account are determined by the plan and configuration associated with your subscription.",
      "Where plan details are presented before purchase, those details form part of the commercial terms applicable to that subscription, subject to these Terms and applicable law.",
    ],
    bullets: [
      "Plan features may differ between subscription levels.",
      "Certain features may be subject to usage or account limits.",
      "Plans may be changed or discontinued subject to applicable notice requirements.",
      "Additional terms may apply to particular products or services.",
    ],
  },

  {
    id: "payments-billing",
    number: "08",
    title: "Payments and Billing",
    paragraphs: [
      "Paid features may require payment of subscription or other applicable charges. The amount, billing interval, currency, taxes, and applicable commercial conditions should be presented through the applicable purchasing or subscription interface.",
      "You authorize the applicable payment process to charge the payment method associated with your subscription for amounts that become due.",
      "If a payment fails, access to paid functionality may be restricted, suspended, or otherwise affected in accordance with the applicable commercial terms.",
      "You are responsible for applicable taxes, duties, bank charges, payment-provider charges, or other amounts that you are legally required to pay, except where otherwise stated.",
    ],
    callouts: [
      {
        type: "info",
        title: "Third-party payment processing",
        text: "Payment processing may involve third-party payment providers. Those providers may impose additional terms, requirements, fees, or privacy practices.",
      },
    ],
  },

  {
    id: "free-trials",
    number: "09",
    title: "Free Trials",
    paragraphs: [
      "Where Universe Inventory offers a free trial or promotional access period, the applicable trial duration, feature limits, eligibility requirements, and conversion conditions will be presented when the offer is made.",
      "A free trial does not create a permanent entitlement to the features made available during the trial.",
      "Unless expressly stated otherwise, Universe Inventory may modify, suspend, or discontinue a trial or promotional offer where permitted by applicable law.",
    ],
  },

  {
    id: "cancellation",
    number: "10",
    title: "Cancellation",
    paragraphs: [
      "You may cancel a subscription through the cancellation mechanism made available by Universe Inventory or by contacting the applicable support or billing channel.",
      "The effect of cancellation, including when paid access ends and whether access continues until the end of a billing period, depends on the applicable subscription terms.",
      "Cancellation does not automatically eliminate amounts that became due before cancellation.",
    ],
  },

  {
    id: "refunds",
    number: "11",
    title: "Refunds",
    paragraphs: [
      "Refund eligibility depends on the refund policy, subscription terms, applicable payment conditions, and mandatory rights provided by applicable law.",
      "Where Universe Inventory maintains a separate refund policy, that policy should be reviewed together with these Terms.",
    ],
    links: [
      {
        label: "Refund Policy",
        value: termsConfig.refundPolicyUrl,
      },
    ],
  },

  {
    id: "acceptable-use",
    number: "12",
    title: "Acceptable Use",
    paragraphs: [
      "You agree to use Universe Inventory responsibly, lawfully, and in a manner that does not interfere with the rights, security, availability, or legitimate operation of the Service or other users.",
      "You are responsible for ensuring that your use of the Service complies with applicable laws and with any applicable third-party requirements.",
    ],
    bullets: [
      "Use the Service for lawful and legitimate business purposes.",
      "Protect account credentials and access controls.",
      "Use integrations and APIs only as permitted.",
      "Respect the rights and data of other users and third parties.",
      "Review important business records before relying on them for material decisions.",
    ],
  },

  {
    id: "prohibited-activities",
    number: "13",
    title: "Prohibited Activities",
    paragraphs: [
      "You must not use the Service to engage in activities that compromise, abuse, unlawfully access, disrupt, or materially interfere with Universe Inventory or its users.",
    ],
    bullets: [
      "Attempting to gain unauthorized access to accounts, systems, networks, or data.",
      "Circumventing security controls or access restrictions.",
      "Introducing malware, malicious code, or harmful software.",
      "Conducting attacks intended to disrupt or degrade the Service.",
      "Attempting to extract or scrape data without authorization.",
      "Impersonating another person, business, or organization.",
      "Using the Service to facilitate fraud or other unlawful conduct.",
      "Attempting to access another customer's private information.",
      "Reverse engineering or decompiling the Service except where such restriction is prohibited by applicable law.",
      "Using the Service in a way that violates third-party rights.",
      "Abusing APIs, integrations, automation mechanisms, or technical interfaces.",
    ],
  },

  {
    id: "inventory-business-data",
    number: "14",
    title: "Inventory and Business Data",
    paragraphs: [
      "Universe Inventory provides tools that may help you manage products, product variants, inventory quantities, warehouses, branches, stock movements, receiving, returns, transfers, adjustments, purchases, sales, expenses, and other operational records.",
      "Software calculations and records depend on information entered into the system, configured rules, transactions, integrations, and other inputs. You remain responsible for reviewing important records and correcting inaccurate information.",
      "Inventory quantities, reports, analytics, calculations, forecasts, or recommendations generated by the Service should not be treated as automatically error-free or as a substitute for appropriate business review.",
    ],
    callouts: [
      {
        type: "info",
        title: "Review important records",
        text: "Always review important inventory, financial, transaction, and business records before relying on them for material business decisions.",
      },
    ],
  },

  {
    id: "pos-transactions",
    number: "15",
    title: "POS and Transactions",
    paragraphs: [
      "Universe Inventory may provide point-of-sale functionality for recording sales and related business transactions.",
      "Depending on the configuration of the Service, transactions may involve cash, bank transfers, payment accounts, multiple payments, refunds, returns, receipts, invoices, or other payment-related records.",
      "You are responsible for confirming that transaction amounts, products, quantities, customer information, payment methods, refunds, and other transaction details are accurate and authorized.",
      "Universe Inventory provides software infrastructure for recording and managing transaction information. It does not independently authorize a transaction on behalf of your business unless a particular third-party service expressly provides that function.",
    ],
  },

  {
    id: "bank-accounts",
    number: "16",
    title: "Bank and Payment Accounts",
    paragraphs: [
      "The Service may allow you to configure information about business bank accounts, payment accounts, POS accounts, or other financial destinations used for transaction records.",
      "You represent that you are authorized to provide and use the account information that you enter into the Service.",
      "Universe Inventory is not itself your bank, financial institution, payment institution, or other financial institution merely because the Service displays or manages information relating to an account.",
      "Where transactions or payment services are provided by a third party, the third party's terms, conditions, fees, security requirements, and privacy practices may also apply.",
    ],
  },

  {
    id: "data-ownership",
    number: "17",
    title: "Data Ownership",
    paragraphs: [
      "Subject to applicable law and the rights of third parties, you retain your rights in the business data and other content that you submit to the Service.",
      "You grant Universe Inventory the permissions reasonably necessary to host, process, transmit, store, display, back up, secure, and otherwise handle that data for the purpose of providing, maintaining, protecting, and improving the Service.",
      "This provision does not transfer ownership of your business records to Universe Inventory.",
    ],
    callouts: [
      {
        type: "success",
        title: "Business data",
        text: "You remain responsible for the accuracy, legality, authorization, and appropriate use of business data submitted to the platform.",
      },
    ],
  },

  {
    id: "data-processing",
    number: "18",
    title: "Data Processing",
    paragraphs: [
      "The Service may process information necessary to create accounts, operate business features, authenticate users, provide support, maintain security, process transactions, and perform other functions requested through the platform.",
      "The specific processing of personal information is governed by the applicable Privacy Policy and any other applicable data-processing terms.",
      "You are responsible for determining what information you enter into the Service and for ensuring that you have an appropriate legal basis and authorization to submit information where required by applicable law.",
    ],
  },

  {
    id: "third-party-services",
    number: "19",
    title: "Third-Party Services",
    paragraphs: [
      "Universe Inventory may connect with or depend upon third-party services, infrastructure, APIs, payment providers, authentication providers, cloud services, messaging services, storage services, analytics services, banking services, or other external systems.",
      "Third-party services may be subject to separate terms and privacy policies. Your use of those services may create a direct relationship between you and the third-party provider.",
      "Universe Inventory does not assume responsibility for terms, functionality, availability, policies, or actions controlled by independent third-party providers.",
    ],
  },

  {
    id: "integrations",
    number: "20",
    title: "Integrations",
    paragraphs: [
      "Integrations may allow information to move between Universe Inventory and external systems.",
      "You are responsible for reviewing the configuration, permissions, destination systems, and data exchanged through an integration before enabling it.",
      "An integration may stop working because of changes to the external service, authentication requirements, APIs, permissions, technical conditions, or other circumstances outside Universe Inventory's reasonable control.",
    ],
  },

  {
    id: "intellectual-property",
    number: "21",
    title: "Intellectual Property",
    paragraphs: [
      `The Service, including its software, user interfaces, designs, visual elements, documentation, branding, logos, trademarks, and related materials, is owned by ${termsConfig.companyName} or its applicable licensors unless otherwise indicated.`,
      "These Terms do not transfer ownership of Universe Inventory intellectual property to you.",
      "Subject to these Terms and any applicable subscription restrictions, you receive a limited, non-exclusive, non-transferable right to use the Service for your legitimate business purposes during the applicable period of authorized access.",
    ],
  },

  {
    id: "user-content",
    number: "22",
    title: "User Content",
    paragraphs: [
      "User Content includes information, records, documents, images, descriptions, product information, customer records, supplier information, inventory records, transaction information, and other materials submitted through the Service.",
      "You are responsible for User Content that you submit, including ensuring that you have the rights, permissions, and authority necessary to submit and use it.",
      "You must not submit content that is unlawful, fraudulent, infringing, malicious, or otherwise prohibited by these Terms or applicable law.",
    ],
  },

  {
    id: "communications",
    number: "23",
    title: "Communications",
    paragraphs: [
      "Universe Inventory may send service-related communications concerning accounts, security, transactions, billing, maintenance, important changes, or other operational matters.",
      "Marketing communications, where applicable, may be subject to separate preferences and applicable law.",
      "You should maintain a valid contact method so that important account and service communications can reach you.",
    ],
  },

  {
    id: "service-availability",
    number: "24",
    title: "Service Availability",
    paragraphs: [
      "Universe Inventory aims to provide a reliable cloud-based service but cannot necessarily guarantee uninterrupted or error-free availability.",
      "Availability may be affected by maintenance, upgrades, network conditions, infrastructure providers, integrations, telecommunications services, security incidents, force majeure events, or circumstances outside reasonable control.",
      termsConfig.serviceAvailability
        ? termsConfig.serviceAvailability
        : "Any service-level commitment, uptime commitment, or availability guarantee will apply only where expressly provided in the applicable commercial agreement or service-level terms.",
    ],
  },

  {
    id: "maintenance",
    number: "25",
    title: "Maintenance",
    paragraphs: [
      "Universe Inventory may perform scheduled or emergency maintenance, upgrades, security changes, infrastructure changes, bug fixes, or other operational work.",
      "Where reasonably practical, advance notice may be provided for planned maintenance that is expected to materially affect availability.",
      "Emergency maintenance may be performed without advance notice where necessary to protect the Service, users, data, or infrastructure.",
    ],
  },

  {
    id: "security",
    number: "26",
    title: "Security",
    paragraphs: [
      "Universe Inventory uses reasonable security measures appropriate to the nature of the Service to help protect the platform and information processed through it.",
      "No internet-connected system can be guaranteed to be completely secure. Users should maintain appropriate account-security practices and use available security controls.",
      "Universe Inventory may monitor system activity where reasonably necessary for security, fraud prevention, troubleshooting, service operation, or protection of the platform.",
    ],
    callouts: [
      {
        type: "success",
        title: "Security responsibility",
        text: "Platform security is a shared responsibility. Protect your credentials and promptly report suspected unauthorized access or security incidents.",
      },
    ],
  },

  {
    id: "privacy",
    number: "27",
    title: "Privacy",
    paragraphs: [
      "The processing of personal information associated with use of Universe Inventory is also governed by the applicable Privacy Policy.",
      "The Privacy Policy should be read together with these Terms because the documents address different aspects of the relationship between you and the Service.",
      "Nothing in these Terms should be interpreted as a substitute for reviewing the applicable privacy requirements for your business, customers, employees, or other individuals whose information you submit.",
    ],
    links: [
      {
        label: "Privacy Policy",
        value: termsConfig.privacyPolicyUrl,
      },
    ],
  },

  {
    id: "data-retention",
    number: "28",
    title: "Data Retention",
    paragraphs: [
      "Data retention practices may depend on account status, product functionality, operational requirements, legal obligations, security requirements, and applicable commercial terms.",
      termsConfig.dataRetentionPeriod
        ? `The configured retention period is ${termsConfig.dataRetentionPeriod}.`
        : "Any specific data-retention period should be stated in the applicable policy, product documentation, or commercial agreement rather than inferred from these Terms.",
      "You should maintain independent copies of important business records where appropriate and where doing so is necessary for your business continuity requirements.",
    ],
  },

  {
    id: "account-suspension",
    number: "29",
    title: "Account Suspension",
    paragraphs: [
      "Universe Inventory may restrict, suspend, or limit access to an account where reasonably necessary to protect the Service, users, data, infrastructure, or legitimate business interests, or where permitted under these Terms.",
      "Circumstances may include suspected fraud, security threats, unlawful activity, material violations of these Terms, abuse of the Service, non-payment, or attempts to compromise platform security.",
      "Where appropriate and legally permitted, Universe Inventory may provide notice and an opportunity to resolve the relevant issue.",
    ],
  },

  {
    id: "account-termination",
    number: "30",
    title: "Account Termination",
    paragraphs: [
      "You may terminate your use of the Service according to the applicable cancellation procedure.",
      "Universe Inventory may terminate or restrict an account where permitted by these Terms or applicable law, including in circumstances involving material violations, unlawful activity, security threats, abuse, or persistent non-payment.",
      "After termination, access to the Service may cease or become restricted. The treatment of remaining data will be governed by applicable policies, technical capabilities, commercial terms, and legal requirements.",
      "If you require an export of important business data, you should seek to obtain it before terminating an account where the applicable functionality permits.",
    ],
  },

  {
    id: "disclaimers",
    number: "31",
    title: "Disclaimers",
    paragraphs: [
      "The Service is provided as software intended to assist with business operations. It does not replace professional judgment or independent verification.",
      "Universe Inventory does not, merely by providing software functionality, act as your accountant, lawyer, tax professional, financial institution, regulatory authority, auditor, or business advisor.",
      "You are responsible for determining whether information generated or stored through the Service is appropriate for your particular business decisions.",
      "To the maximum extent permitted by applicable law, any warranties or conditions not expressly provided in these Terms are excluded to the extent such exclusion is legally permitted.",
    ],
    callouts: [
      {
        type: "warning",
        title: "Important business decisions",
        text: "Review important financial, tax, legal, inventory, and operational information with an appropriately qualified professional where necessary.",
      },
    ],
  },

  {
    id: "limitation-of-liability",
    number: "32",
    title: "Limitation of Liability",
    paragraphs: [
      "To the maximum extent permitted by applicable law, Universe Inventory and its applicable affiliates, officers, employees, contractors, licensors, and service providers will not be liable for losses that cannot legally be excluded or limited only to the extent such limitation is permitted.",
      "Any specific exclusions, caps, categories of damages, or contractual liability limitations should be finalized based on the governing jurisdiction, commercial structure, and advice of qualified legal counsel.",
      "Nothing in these Terms is intended to exclude or limit liability where applicable law prohibits such exclusion or limitation.",
    ],
  },

  {
    id: "indemnification",
    number: "33",
    title: "Indemnification",
    paragraphs: [
      "To the extent permitted by applicable law, you may be responsible for claims, losses, liabilities, damages, costs, or expenses arising from your unlawful use of the Service, violation of these Terms, infringement of third-party rights, or unauthorized use of User Content.",
      "The scope of any indemnification obligation should be interpreted subject to applicable law and any separately agreed commercial terms.",
    ],
  },

  {
    id: "force-majeure",
    number: "34",
    title: "Force Majeure",
    paragraphs: [
      "Neither party should be considered responsible for failure or delay in performing obligations to the extent caused by circumstances reasonably beyond that party's control, subject to applicable law.",
      "Such circumstances may include significant infrastructure failures, natural disasters, telecommunications failures, widespread service disruptions, governmental actions, civil emergencies, labor disruptions, or other events outside reasonable control.",
    ],
  },

  {
    id: "changes",
    number: "35",
    title: "Changes to These Terms",
    paragraphs: [
      "Universe Inventory may update these Terms from time to time where appropriate, including to reflect changes to the Service, business practices, legal requirements, security requirements, or commercial arrangements.",
      "Where required by applicable law, reasonable notice may be provided for material changes.",
      "The effective date and version displayed at the beginning of the document should be updated whenever a new version becomes effective.",
    ],
  },

  {
    id: "governing-law",
    number: "36",
    title: "Governing Law",
    paragraphs: [
      "The governing law and jurisdiction applicable to these Terms should be expressly configured and reviewed by qualified legal counsel before publication.",
    ],
    definition: {
      label: "Governing Law",
      value: displayConfigValue(termsConfig.governingLaw),
    },
    definitionTwo: {
      label: "Jurisdiction",
      value: displayConfigValue(termsConfig.jurisdiction),
    },
  },

  {
    id: "dispute-resolution",
    number: "37",
    title: "Dispute Resolution",
    paragraphs: [
      "Any dispute arising from or relating to the Service or these Terms should be handled in accordance with the dispute-resolution procedure applicable to the relationship and governing jurisdiction.",
      "Where specific arbitration, mediation, court jurisdiction, notice, or escalation requirements are intended, those provisions should be finalized and reviewed by qualified legal counsel before publication.",
    ],
  },

  {
    id: "contact-information",
    number: "38",
    title: "Contact Information",
    paragraphs: [
      "If you have questions about these Terms, your account, billing, or legal matters relating to the Service, use the appropriate contact channel provided by Universe Inventory.",
    ],
    contacts: [
      {
        title: "Legal",
        description: "For legal notices and Terms-related questions.",
        email: termsConfig.legalEmail,
      },
      {
        title: "Support",
        description: "For account and technical assistance.",
        email: termsConfig.supportEmail,
      },
      {
        title: "General Enquiries",
        description: "For general business enquiries.",
        email: termsConfig.generalEmail,
      },
    ],
  },
];


/*
 * ============================================================
 * MAIN COMPONENT
 * ============================================================
 */

function TermsOfService() {
  const [searchValue, setSearchValue] = useState("");
  const [activeSection, setActiveSection] = useState(
    sections[0]?.id || ""
  );
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [copiedSection, setCopiedSection] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);

  const sectionRefs = useRef({});

  /*
   * Search matching
   */
  const searchResults = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    if (!query) {
      return [];
    }

    return sections.filter((section) => {
      const searchableText = [
        section.title,
        ...(section.paragraphs || []),
        ...(section.bullets || []),
        ...(section.callouts || []).map(
          (callout) => `${callout.title} ${callout.text}`
        ),
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [searchValue]);

  const hasSearch = searchValue.trim().length > 0;

  /*
   * Highlight matching search text.
   */
  const highlightText = (text) => {
    if (!hasSearch || typeof text !== "string") {
      return text;
    }

    const query = searchValue.trim();

    if (!query) {
      return text;
    }

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedQuery})`, "gi");

    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={`${part}-${index}`}>{part}</mark>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      )
    );
  };

  /*
   * Smooth scroll to section.
   */
  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];

    if (!element) {
      return;
    }

    const headerOffset = 32;

    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: Math.max(0, elementPosition - headerOffset),
      behavior: "smooth",
    });

    setActiveSection(sectionId);
    setMobileTocOpen(false);

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}#${sectionId}`
    );
  };

  /*
   * IntersectionObserver for active section.
   */
  useEffect(() => {
    const elements = sections
      .map((section) => sectionRefs.current[section.id])
      .filter(Boolean);

    if (!elements.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) =>
              a.boundingClientRect.top - b.boundingClientRect.top
          );

        if (visibleEntries.length) {
          const nextId = visibleEntries[0].target.id;
          setActiveSection(nextId);
        }
      },
      {
        root: null,
        rootMargin: "-15% 0px -65% 0px",
        threshold: [0, 0.1, 0.25],
      }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  /*
   * Read initial hash.
   */
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");

    if (!hash) {
      return;
    }

    const timeout = window.setTimeout(() => {
      scrollToSection(hash);
    }, 150);

    return () => window.clearTimeout(timeout);
  }, []);

  /*
   * Back-to-top visibility.
   */
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /*
   * Copy section URL.
   */
  const handleCopyLink = async (sectionId) => {
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}#${sectionId}`;

    try {
      await navigator.clipboard.writeText(url);

      setCopiedSection(sectionId);

      window.setTimeout(() => {
        setCopiedSection("");
      }, 1800);
    } catch (error) {
      /*
       * Clipboard may be unavailable in some browser/security
       * environments. Fall back to changing the URL.
       */
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#${sectionId}`
      );

      setCopiedSection(sectionId);

      window.setTimeout(() => {
        setCopiedSection("");
      }, 1800);
    }
  };

  /*
   * Print
   */
  const handlePrint = () => {
    window.print();
  };

  /*
   * Back to top
   */
  const handleBackToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /*
   * Contact email handling.
   */
  const handleEmail = (email) => {
    if (!email) {
      return;
    }

    window.location.href = `mailto:${email}`;
  };

  return (
    <main className="terms-page">
      <div className="terms-page__shell">
        {/* ====================================================
            HEADER
        ==================================================== */}

        <header className="terms-hero">
          <div className="terms-hero__top fx fx-ac fx-jb wrap g3">
            <div className="terms-brand fx fx-ac g2">
              <div
                className="terms-brand__icon"
                aria-hidden="true"
              >
                <MenuBook />
              </div>

              <div>
                <div className="terms-brand__name fw600">
                  {termsConfig.companyName}
                </div>

                <div className="terms-brand__label">
                  Legal &amp; Documentation
                </div>
              </div>
            </div>

            <div className="terms-status fx fx-ac g1">
              <CheckCircle aria-hidden="true" />
              <span>{termsConfig.status}</span>
            </div>
          </div>

          <div className="terms-hero__content">
            <div className="terms-hero__eyebrow fx fx-ac g1">
              <Gavel aria-hidden="true" />
              <span>LEGAL DOCUMENT</span>
            </div>

            <h1 className="terms-hero__title">
              {termsConfig.documentTitle}
            </h1>

            <p className="terms-hero__description">
              Rules and conditions governing your use of{" "}
              {termsConfig.companyName}.
            </p>

            <div className="terms-meta fx wrap g2">
              <div className="terms-meta__item">
                <span>Effective Date</span>
                <strong>
                  {displayConfigValue(
                    termsConfig.effectiveDate,
                    "To be configured"
                  )}
                </strong>
              </div>

              <div className="terms-meta__item">
                <span>Last Updated</span>
                <strong>
                  {displayConfigValue(
                    termsConfig.lastUpdated,
                    "To be configured"
                  )}
                </strong>
              </div>

              <div className="terms-meta__item">
                <span>Version</span>
                <strong>{termsConfig.version}</strong>
              </div>

              <div className="terms-meta__item">
                <span>Status</span>
                <strong className="terms-meta__current">
                  <CheckCircle aria-hidden="true" />
                  {termsConfig.status}
                </strong>
              </div>
            </div>
          </div>
        </header>

        {/* ====================================================
            NOTICE
        ==================================================== */}

        <section className="terms-review-notice fx fx-ac g2">
          <div className="terms-review-notice__icon">
            <InfoOutlined aria-hidden="true" />
          </div>

          <div className="fg1">
            <strong>Legal review recommended</strong>

            <p>
              This document is structured as a commercial SaaS Terms
              of Service template. Jurisdiction-specific provisions,
              commercial policies, and final legal language should be
              reviewed and finalized by qualified legal counsel before
              publication.
            </p>
          </div>
        </section>

        {/* ====================================================
            SEARCH
        ==================================================== */}

        <section className="terms-search">
          <div className="terms-search__box fx fx-ac g2">
            <Search aria-hidden="true" />

            <input
              type="search"
              value={searchValue}
              onChange={(event) =>
                setSearchValue(event.target.value)
              }
              placeholder="Search Terms of Service"
              aria-label="Search Terms of Service"
            />

            {searchValue && (
              <button
                type="button"
                className="terms-search__clear"
                onClick={() => setSearchValue("")}
                aria-label="Clear search"
              >
                <Close aria-hidden="true" />
              </button>
            )}
          </div>

          {hasSearch && (
            <div className="terms-search__results">
              <span>
                {searchResults.length}{" "}
                {searchResults.length === 1 ? "result" : "results"} found
              </span>
            </div>
          )}
        </section>

        {/* ====================================================
            MOBILE TOC
        ==================================================== */}

        <div className="terms-mobile-toc">
          <button
            type="button"
            className="terms-mobile-toc__button fx fx-ac fx-jb"
            onClick={() => setMobileTocOpen((current) => !current)}
            aria-expanded={mobileTocOpen}
            aria-controls="terms-mobile-navigation"
          >
            <span className="fx fx-ac g2">
              <MenuBook aria-hidden="true" />
              Table of Contents
            </span>

            <ExpandMore
              className={
                mobileTocOpen
                  ? "terms-mobile-toc__arrow terms-mobile-toc__arrow--open"
                  : "terms-mobile-toc__arrow"
              }
              aria-hidden="true"
            />
          </button>

          {mobileTocOpen && (
            <nav
              id="terms-mobile-navigation"
              className="terms-mobile-toc__content"
              aria-label="Terms of Service sections"
            >
              {sections.map((section) => (
                <button
                  type="button"
                  key={section.id}
                  className={
                    activeSection === section.id
                      ? "terms-toc__item terms-toc__item--active"
                      : "terms-toc__item"
                  }
                  onClick={() => scrollToSection(section.id)}
                >
                  <span>{section.number}</span>
                  <strong>{section.title}</strong>
                </button>
              ))}
            </nav>
          )}
        </div>

        {/* ====================================================
            DOCUMENT BODY
        ==================================================== */}

        <div className="terms-layout">
          {/* ==================================================
              DESKTOP TOC
          ================================================== */}

          <aside className="terms-sidebar">
            <div className="terms-sidebar__inner">
              <div className="terms-sidebar__heading fx fx-ac g2">
                <MenuBook aria-hidden="true" />

                <div>
                  <strong>Table of Contents</strong>
                  <span>{sections.length} sections</span>
                </div>
              </div>

              <nav
                className="terms-toc"
                aria-label="Terms of Service sections"
              >
                {sections.map((section) => (
                  <button
                    type="button"
                    key={section.id}
                    className={
                      activeSection === section.id
                        ? "terms-toc__item terms-toc__item--active"
                        : "terms-toc__item"
                    }
                    onClick={() => scrollToSection(section.id)}
                  >
                    <span className="terms-toc__number">
                      {section.number}
                    </span>

                    <span className="terms-toc__title">
                      {section.title}
                    </span>

                    {activeSection === section.id && (
                      <ChevronRight
                        className="terms-toc__chevron"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* ==================================================
              CONTENT
          ================================================== */}

          <article className="terms-document">
            {sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                ref={(element) => {
                  sectionRefs.current[section.id] = element;
                }}
                className={
                  hasSearch && searchResults.includes(section)
                    ? "terms-section terms-section--search-match"
                    : "terms-section"
                }
              >
                <div className="terms-section__header">
                  <div className="terms-section__number">
                    § {section.number}
                  </div>

                  <div className="terms-section__heading-wrap">
                    <h2>{highlightText(section.title)}</h2>

                    <button
                      type="button"
                      className="terms-section__link"
                      onClick={() => handleCopyLink(section.id)}
                      aria-label={`Copy link to ${section.title}`}
                      title="Copy section link"
                    >
                      <Link aria-hidden="true" />

                      <span>
                        {copiedSection === section.id
                          ? "Link copied"
                          : "Copy link"}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="terms-section__body">
                  {(section.paragraphs || []).map(
                    (paragraph, index) => (
                      <p key={`${section.id}-p-${index}`}>
                        {highlightText(paragraph)}
                      </p>
                    )
                  )}

                  {section.definition && (
                    <div className="terms-definition">
                      <span>{section.definition.label}</span>
                      <strong>{section.definition.value}</strong>
                    </div>
                  )}

                  {section.definitionTwo && (
                    <div className="terms-definition">
                      <span>{section.definitionTwo.label}</span>
                      <strong>
                        {section.definitionTwo.value}
                      </strong>
                    </div>
                  )}

                  {section.bullets?.length > 0 && (
                    <ul className="terms-list">
                      {section.bullets.map((bullet, index) => (
                        <li key={`${section.id}-b-${index}`}>
                          <span className="terms-list__marker">
                            <CheckCircle aria-hidden="true" />
                          </span>

                          <span>{highlightText(bullet)}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.callouts?.map((callout, index) => (
                    <div
                      key={`${section.id}-callout-${index}`}
                      className={`terms-callout terms-callout--${callout.type}`}
                    >
                      <div className="terms-callout__icon">
                        {callout.type === "warning" ? (
                          <WarningAmber aria-hidden="true" />
                        ) : callout.type === "success" ? (
                          <CheckCircle aria-hidden="true" />
                        ) : (
                          <InfoOutlined aria-hidden="true" />
                        )}
                      </div>

                      <div>
                        <strong>{callout.title}</strong>
                        <p>{highlightText(callout.text)}</p>
                      </div>
                    </div>
                  ))}

                  {section.links?.map((item) => (
                    <div
                      className="terms-related-link"
                      key={item.label}
                    >
                      {item.value ? (
                        <a
                          href={item.value}
                          target="_self"
                          rel="noreferrer"
                        >
                          {item.label}
                          <ChevronRight aria-hidden="true" />
                        </a>
                      ) : (
                        <span className="terms-related-link__disabled">
                          {item.label}
                          <span>Link to be configured</span>
                        </span>
                      )}
                    </div>
                  ))}

                  {section.contacts && (
                    <div className="terms-contact-grid">
                      {section.contacts.map((contact) => (
                        <div
                          className="terms-contact-card"
                          key={contact.title}
                        >
                          <div className="terms-contact-card__icon">
                            <MailOutline aria-hidden="true" />
                          </div>

                          <div className="terms-contact-card__content">
                            <strong>{contact.title}</strong>

                            <p>{contact.description}</p>

                            {contact.email ? (
                              <button
                                type="button"
                                onClick={() =>
                                  handleEmail(contact.email)
                                }
                              >
                                {contact.email}
                              </button>
                            ) : (
                              <span className="terms-contact-card__placeholder">
                                Email to be configured
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            ))}

            {/* ==================================================
                FINAL LEGAL FOOTER
            ================================================== */}

            <section className="terms-final-card">
              <div className="terms-final-card__icon">
                <Gavel aria-hidden="true" />
              </div>

              <div className="fg1">
                <h2>Terms of Service</h2>

                <p>
                  {termsConfig.companyName} — Version{" "}
                  {termsConfig.version}
                </p>

                <p>
                  Please ensure that the final published version
                  contains all required commercial, legal, contact,
                  jurisdictional, privacy, billing, and policy
                  information applicable to your business.
                </p>
              </div>
            </section>
          </article>
        </div>
      </div>

      {/* ======================================================
          ACTIONS
      ====================================================== */}

      <div className="terms-floating-actions">
        {showBackToTop && (
          <button
            type="button"
            className="terms-floating-button"
            onClick={handleBackToTop}
            aria-label="Back to top"
            title="Back to top"
          >
            <ArrowUpward aria-hidden="true" />
          </button>
        )}

        <button
          type="button"
          className="terms-floating-button terms-floating-button--print"
          onClick={handlePrint}
          aria-label="Print Terms of Service"
          title="Print Terms"
        >
          <Print aria-hidden="true" />
        </button>
      </div>

      {/* ======================================================
          PRINT ACTION BAR
      ====================================================== */}

      <div className="terms-print-bar">
        <button
          type="button"
          className="terms-print-button fx fx-ac fx-jc g1"
          onClick={handlePrint}
        >
          <Print aria-hidden="true" />
          Print Terms
        </button>
      </div>
    </main>
  );
}

export default TermsOfService;